export const FIREBASE_LOGIN_PATH = '/auth/firebase/login';
export const FIREBASE_LOGIN_METHOD = 'POST';
export const ACCOUNT_DELETE_PATH = '/account';

export const PROVIDER_GOOGLE = 'google.com';
export const PROVIDER_APPLE = 'apple.com';

export const DELETION_STAGES = {
  CANCELED: 'canceled',
  REAUTH_FAILED: 'reauth_failed',
  APPLE_REVOKE_FAILED: 'apple_revoke_failed',
  BACKEND_FAILED: 'backend_failed',
  FIREBASE_DELETE_FAILED: 'firebase_delete_failed',
  COMPLETE: 'complete',
};

export function isNativePlatform(capacitor) {
  return Boolean(capacitor && typeof capacitor.isNativePlatform === 'function' && capacitor.isNativePlatform());
}

export function isNativeIos(capacitor) {
  if (!isNativePlatform(capacitor)) return false;
  if (typeof capacitor.getPlatform !== 'function') return false;
  return capacitor.getPlatform() === 'ios';
}

export function shouldUseNativeGoogle(capacitor) {
  return isNativeIos(capacitor);
}

export function shouldUseNativeApple(capacitor) {
  return isNativeIos(capacitor);
}

export const FIREBASE_AUTH_PLUGIN = 'FirebaseAuthentication';
export const NATIVE_SIGN_IN_TIMEOUT_MS = 90000;

export function withTimeout(promise, ms, message) {
  let timer;
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => {
      const error = new Error(message);
      error.code = 'NATIVE_SIGN_IN_TIMEOUT';
      reject(error);
    }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => {
    clearTimeout(timer);
  });
}

export function isAuthCanceled(error) {
  if (!error) return false;
  const code = String(error.code || error.errorCode || '').toLowerCase();
  const message = String(error.message || error.errorMessage || '').toLowerCase();
  const combined = `${code} ${message}`;
  if (error.code === 'ERR_CANCELED' || error.code === '12501' || error.code === 12501) return true;
  if (error.code === '1001' || error.code === 1001) return true;
  return (
    combined.includes('cancel') ||
    combined.includes('canceled') ||
    combined.includes('cancelled')
  );
}

export function isPluginUnimplemented(error) {
  if (!error) return false;
  const code = String(error.code || error.errorCode || '');
  if (code === 'UNIMPLEMENTED') return true;
  const message = String(error.message || error.errorMessage || '');
  return message.includes('UNIMPLEMENTED') || message.includes('"code":"UNIMPLEMENTED"');
}

export function assertNativeAuthPluginAvailable(capacitor) {
  if (!capacitor || typeof capacitor.isNativePlatform !== 'function') return;
  if (!capacitor.isNativePlatform()) return;
  if (typeof capacitor.isPluginAvailable === 'function' && !capacitor.isPluginAvailable(FIREBASE_AUTH_PLUGIN)) {
    const error = new Error('FirebaseAuthentication plugin is not available in this app build.');
    error.code = 'UNIMPLEMENTED';
    throw error;
  }
}

export function normalizeProviderError(error, providerLabel) {
  if (isAuthCanceled(error)) return null;
  if (isPluginUnimplemented(error)) {
    return `${providerLabel} sign-in is not available in this app build. Update the app and try again.`;
  }
  const status = error?.response?.status;
  if (status === 401 || status === 403) {
    return 'Could not verify your sign-in. Please try again.';
  }
  if (status >= 500) {
    return 'The server could not complete sign-in. Please try again.';
  }
  const message = String(error?.message || '');
  if (error?.code === 'NATIVE_SIGN_IN_TIMEOUT' || message.includes('NATIVE_SIGN_IN_TIMEOUT')) {
    return `${providerLabel} sign-in did not complete. Please try again.`;
  }
  if (message.includes('MISSING_ID_TOKEN') || message.includes('NATIVE_MISSING_ID_TOKEN')) {
    return `${providerLabel} sign-in did not return a verified credential. Please try again.`;
  }
  return `${providerLabel} sign-in failed. Please try again.`;
}

export function settleProviderAuthUi(error, providerLabel) {
  return {
    isLoading: false,
    canceled: isAuthCanceled(error),
    providerError: isAuthCanceled(error) ? '' : (normalizeProviderError(error, providerLabel) || ''),
  };
}

export async function runSettledProviderAttempt(operation, providerLabel) {
  try {
    await operation();
    return { ok: true, isLoading: false, canceled: false, providerError: '' };
  } catch (error) {
    return { ok: false, ...settleProviderAuthUi(error, providerLabel) };
  }
}

export function extractAppleRevokeToken(result) {
  if (!result) return null;
  const credential = result.credential || result;
  const code = credential.authorizationCode || result.authorizationCode;
  if (typeof code === 'string' && code.length > 0) return code;
  const access = credential.accessToken || result.accessToken;
  if (typeof access === 'string' && access.length > 0) return access;
  return null;
}

export function resolveNativeProviderId(user) {
  if (!user) return null;
  const fromData = Array.isArray(user.providerData)
    ? user.providerData.map((entry) => entry && entry.providerId).filter(Boolean)
    : [];
  if (fromData.includes(PROVIDER_APPLE)) return PROVIDER_APPLE;
  if (fromData.includes(PROVIDER_GOOGLE)) return PROVIDER_GOOGLE;
  if (user.providerId === PROVIDER_APPLE || user.providerId === PROVIDER_GOOGLE) {
    return user.providerId;
  }
  return null;
}

export function firebaseLoginHeaders(idToken) {
  return {
    Authorization: `Bearer ${idToken}`,
  };
}

export function deletionNoticeStorageKey() {
  return 'bst_deletion_notice';
}

export function deletionBackendMessage(error, steps) {
  const status = error?.response?.status;
  if (steps?.appleRevoked) {
    if (status >= 500) {
      return 'We could not delete your BarbershopTalk account. Your Apple sign-in may need to be set up again. Try again or contact support.';
    }
    if (status === 401 || status === 403) {
      return 'Please sign in again, then retry account deletion. Your Apple sign-in may need to be set up again.';
    }
  }
  if (status === 401 || status === 403) {
    return 'Please sign in again, then retry account deletion.';
  }
  if (status === 400) {
    return 'Account deletion was rejected. Check your confirmation and password, then try again.';
  }
  if (status >= 500) {
    return 'The server could not delete your account. Please try again.';
  }
  if (!error?.response) {
    return 'Network error during account deletion. Your account may still exist. Please try again.';
  }
  return 'Could not delete your account.';
}

export function firebaseDeletePartialMessage() {
  return 'Your BarbershopTalk account was closed, but we could not fully remove the sign-in provider. Sign out and contact support if you can still sign in.';
}

export async function orchestrateAccountDeletion(deps) {
  const steps = {
    appleRevoked: false,
    backendDeleted: false,
    firebaseDeleted: false,
    signedOut: false,
  };

  const isNative = Boolean(deps.isNative);
  const providerId = deps.providerId || null;

  if (isNative && providerId === PROVIDER_APPLE) {
    let appleResult;
    try {
      appleResult = await deps.reauthenticateApple();
    } catch (error) {
      if (isAuthCanceled(error)) {
        return { ok: false, stage: DELETION_STAGES.CANCELED, message: null, steps };
      }
      return {
        ok: false,
        stage: DELETION_STAGES.REAUTH_FAILED,
        message: 'Apple could not confirm it was you. Account deletion was not started.',
        steps,
      };
    }
    const revokeToken = extractAppleRevokeToken(appleResult);
    if (revokeToken) {
      try {
        await deps.revokeAppleToken(revokeToken);
        steps.appleRevoked = true;
      } catch {
        return {
          ok: false,
          stage: DELETION_STAGES.APPLE_REVOKE_FAILED,
          message: 'Apple sign-in could not be revoked. Your BarbershopTalk account was not deleted. Try again.',
          steps,
        };
      }
    }
  }

  if (isNative && providerId === PROVIDER_GOOGLE) {
    try {
      await deps.reauthenticateGoogle();
    } catch (error) {
      if (isAuthCanceled(error)) {
        return { ok: false, stage: DELETION_STAGES.CANCELED, message: null, steps };
      }
      return {
        ok: false,
        stage: DELETION_STAGES.REAUTH_FAILED,
        message: 'Google could not confirm it was you. Account deletion was not started.',
        steps,
      };
    }
  }

  let backendResponse;
  try {
    backendResponse = await deps.deleteBackendAccount({
      confirmation: deps.confirmation,
      password: deps.password,
    });
  } catch (error) {
    return {
      ok: false,
      stage: DELETION_STAGES.BACKEND_FAILED,
      message: deletionBackendMessage(error, steps),
      status: error?.response?.status,
      steps,
    };
  }

  const backendOk = backendResponse?.status === 204 || backendResponse?.status === 200;
  if (!backendOk) {
    return {
      ok: false,
      stage: DELETION_STAGES.BACKEND_FAILED,
      message: 'The server did not confirm account deletion. Your account may still exist.',
      steps,
    };
  }
  steps.backendDeleted = true;

  try {
    await deps.deleteFirebaseUser();
    steps.firebaseDeleted = true;
  } catch {
    try {
      await deps.signOutAll();
      steps.signedOut = true;
    } catch {
      steps.signedOut = false;
    }
    return {
      ok: false,
      stage: DELETION_STAGES.FIREBASE_DELETE_FAILED,
      message: firebaseDeletePartialMessage(),
      steps,
    };
  }

  try {
    await deps.signOutAll();
    steps.signedOut = true;
  } catch {
    steps.signedOut = false;
  }

  return { ok: true, stage: DELETION_STAGES.COMPLETE, steps };
}
